import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// The Customise page (TMS-948): a member toggles apps between "In my Sidebar" and
// "Hidden" and drags rows to reorder, and the rail updates without a reload.
//
// Every change is optimistic — the row moves first, then saves — and each save
// sends the COMPLETE lists rather than a delta, which is what makes a retry or a
// second tab harmless. A failed save rolls the DOM back to the snapshot taken
// before the change, so the page never shows state the server didn't accept.
//
// Turbo Drive is disabled app-wide (<body data-turbo="false">), so a form would do
// a full page submit. Saving is a fetch whose turbo-stream response is applied by
// hand, the same shape as inline_select_controller and workplace/home_controller.
export default class extends Controller {
  static targets = [
    "lists", "visibleList", "hiddenList", "visibleCount", "hiddenCount",
    "hiddenEmpty", "resetButton", "error", "announcer", "title", "row", "handle"
  ]
  static values = { url: String }

  connect() {
    this.keyboardSaveDelay = 400
  }

  disconnect() {
    clearTimeout(this.pendingKeyboardSave)
  }

  // Toggling

  toggle(event) {
    const row = event.target.closest("[data-key]")
    if (!row) return

    const showing = event.target.checked
    const destination = showing ? this.visibleListTarget : this.hiddenListTarget

    this.snapshotLists()
    this.rememberFocus(row, { handle: false })
    destination.appendChild(row)
    this.refreshCounts()
    this.announce(`${this.labelOf(row)} ${showing ? "added to" : "hidden from"} your Sidebar.`)
    this.save()
  }

  // Dragging

  dragStart(event) {
    const row = event.target.closest("[data-key]")
    if (!row || row.classList.contains("is-pinned")) return

    this.snapshotLists()
    this.orderBeforeDrag = this.keysIn(row.parentElement).join()
    this.dragged = row
    this.dragOrigin = row.parentElement
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", "")
    // Deferred so the drag image is the full-opacity row, not the faded one.
    requestAnimationFrame(() => row.classList.add("is-dragging"))
  }

  dragOver(event) {
    if (!this.dragged) return
    // Reordering is scoped to the list the drag started in; moving between the two
    // sections is what the toggle is for.
    if (event.currentTarget !== this.dragOrigin) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const target = this.rowUnder(event.currentTarget, event.clientY)
    if (target === this.dragged) return

    if (target) {
      if (this.wouldDisplacePinned(target)) return
      event.currentTarget.insertBefore(this.dragged, target)
    } else if (event.currentTarget.lastElementChild !== this.dragged) {
      event.currentTarget.appendChild(this.dragged)
    }
  }

  drop(event) {
    if (!this.dragged) return
    event.preventDefault()
  }

  dragEnd() {
    if (!this.dragged) return

    const row = this.dragged
    const list = this.dragOrigin
    row.classList.remove("is-dragging")
    this.dragged = null
    this.dragOrigin = null

    // Picking a row up and dropping it back is not a change worth a round-trip.
    if (this.keysIn(list).join() === this.orderBeforeDrag) return

    this.rememberFocus(row, { handle: true })
    this.announce(`${this.labelOf(row)} moved to position ${this.positionOf(row)}.`)
    this.save()
  }

  // Keyboard reordering — the only accessible path, since dragging is pointer-only.

  moveWithKeys(event) {
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return

    const row = event.target.closest("[data-key]")
    if (!row || row.classList.contains("is-pinned")) return

    const sibling = event.key === "ArrowUp" ? row.previousElementSibling : row.nextElementSibling
    if (!sibling) return
    if (event.key === "ArrowUp" && this.wouldDisplacePinned(sibling)) return

    event.preventDefault()
    // Only the first move of a run snapshots, so a rollback undoes the whole run
    // rather than leaving the member half-way through it.
    if (!this.pendingKeyboardSave) this.snapshotLists()
    this.rememberFocus(row, { handle: true })
    if (event.key === "ArrowUp") {
      sibling.before(row)
    } else {
      sibling.after(row)
    }
    event.target.focus()
    this.announce(`${this.labelOf(row)} moved to position ${this.positionOf(row)}.`)

    // A run of arrow presses is one request rather than one per keystroke.
    clearTimeout(this.pendingKeyboardSave)
    this.pendingKeyboardSave = setTimeout(() => {
      this.pendingKeyboardSave = null
      this.save()
    }, this.keyboardSaveDelay)
  }

  // Saving

  reset() {
    this.resetButtonTarget.disabled = true
    this.request({ reset: true })
      .then(() => {
        this.announce("Sidebar reset to default.")
        this.titleTarget.focus()
      })
      .catch(() => {
        this.resetButtonTarget.disabled = false
        this.showError()
      })
  }

  save() {
    return this.request({ reset: false }).catch(() => {
      this.rollback()
      this.showError()
    })
  }

  async request({ reset }) {
    this.hideError()

    const body = new FormData()
    body.append("_method", reset ? "DELETE" : "PATCH")
    if (!reset) {
      this.keysIn(this.visibleListTarget).forEach(key => body.append("order[]", key))
      this.keysIn(this.hiddenListTarget).forEach(key => body.append("hidden[]", key))
    }

    const response = await fetch(this.urlValue, {
      method: "POST",
      body,
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        "Accept": "text/vnd.turbo-stream.html"
      }
    })

    if (!response.ok) throw new Error(`Save failed (${response.status})`)

    Turbo.renderStreamMessage(await response.text())
    this.listsSnapshot = undefined
    // The button lives outside the replaced region, so the stream can't enable it:
    // the member has now customised something, so a reset is meaningful.
    if (!reset) this.resetButtonTarget.disabled = false
    return response
  }

  // Focus survival. turbo_stream.replace swaps out the whole list, taking the
  // focused control with it — which would drop focus to <body> after every save.
  // Arrow keys are the ONLY accessible way to reorder, so losing focus each time
  // would cost a keyboard user a full re-tab between every single move.

  // Fires exactly when the replacement list is inserted, which is the only reliable
  // moment to restore focus: Turbo renders a stream asynchronously, so anything
  // scheduled right after renderStreamMessage still sees the outgoing DOM.
  listsTargetConnected() {
    this.restoreFocus()
  }

  rememberFocus(row, { handle }) {
    this.focusMemo = { key: row.dataset.key, handle }
  }

  restoreFocus() {
    if (!this.focusMemo) return

    const { key, handle } = this.focusMemo
    this.focusMemo = null
    const control = handle ? ".nav-customize-handle" : ".toggle-input"
    this.element.querySelector(`[data-key="${key}"] ${control}`)?.focus()
  }

  // Optimistic-change bookkeeping. Restoring the whole markup rather than moving
  // the row back covers all three interactions with one mechanism, and restores the
  // toggles' checked state for free because it is the server-rendered markup.

  snapshotLists() {
    this.listsSnapshot = this.listsTarget.innerHTML
  }

  rollback() {
    if (this.listsSnapshot === undefined) return

    this.listsTarget.innerHTML = this.listsSnapshot
    this.listsSnapshot = undefined
    this.announce("Couldn't save. Your Sidebar is unchanged.")
  }

  // Helpers

  refreshCounts() {
    const visible = this.keysIn(this.visibleListTarget).length
    const hidden = this.keysIn(this.hiddenListTarget).length
    this.visibleCountTarget.textContent = this.appCount(visible)
    this.hiddenCountTarget.textContent = this.appCount(hidden)
    if (this.hasHiddenEmptyTarget) this.hiddenEmptyTarget.hidden = hidden > 0
  }

  appCount(count) {
    return `${count} ${count === 1 ? "App" : "Apps"}`
  }

  keysIn(list) {
    return Array.from(list.querySelectorAll(":scope > [data-key]")).map(row => row.dataset.key)
  }

  rowUnder(list, pointerY) {
    return Array.from(list.querySelectorAll(":scope > [data-key]"))
      .filter(row => row !== this.dragged)
      .find(row => {
        const box = row.getBoundingClientRect()
        return pointerY < box.top + box.height / 2
      })
  }

  // My Hub is pinned to the top. The server re-pins it regardless, so this only
  // spares the member a move that would visibly snap back.
  wouldDisplacePinned(target) {
    return target.classList.contains("is-pinned")
  }

  positionOf(row) {
    const rows = Array.from(row.parentElement.querySelectorAll(":scope > [data-key]"))
    return `${rows.indexOf(row) + 1} of ${rows.length}`
  }

  labelOf(row) {
    return row.querySelector(".nav-customize-label")?.textContent.trim() || "App"
  }

  announce(message) {
    if (this.hasAnnouncerTarget) this.announcerTarget.textContent = message
  }

  showError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = false
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }
}
